import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../services/supabase';
import { activateStudentDemo, deactivateStudentDemo } from '../services/studentDemoMode';

// Create StudentAuthContext
const StudentAuthContext = createContext();

// Custom hook to use StudentAuthContext
export function useStudentAuth() {
  return useContext(StudentAuthContext);
}

// StudentAuthProvider component
export function StudentAuthProvider({ children }) {
  const [studentUser, setStudentUser] = useState(null);
  const [studentClasses, setStudentClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-school feature-flags (scholen.features JSONB) — zelfde semantiek als het
  // docentportaal: alles staat aan, alleen expliciet false verbergt.
  const [schoolFeatures, setSchoolFeatures] = useState(null);

  useEffect(() => {
    if (!studentUser?.school_id) { setSchoolFeatures(null); return; }
    // Vorige-keer-gecachede flags meteen gebruiken → geen nav/hero-flikkering
    // bij terugkerende leerlingen terwijl de verse fetch loopt.
    const cacheKey = `tw-school-features-${studentUser.school_id}`;
    try { const c = localStorage.getItem(cacheKey); if (c) setSchoolFeatures(JSON.parse(c)); } catch (_) {}
    let stop = false;
    supabase.from('scholen').select('features').eq('id', studentUser.school_id).maybeSingle()
      .then(({ data }) => {
        if (stop) return;
        const f = data?.features || {};
        setSchoolFeatures(f);
        try { localStorage.setItem(cacheKey, JSON.stringify(f)); } catch (_) {}
      })
      .catch(() => { if (!stop) setSchoolFeatures((prev) => prev ?? {}); });
    return () => { stop = true; };
  }, [studentUser?.school_id]);

  function heeftFeature(key) {
    return !(schoolFeatures && schoolFeatures[key] === false);
  }

  // Haal student data op uit Supabase leerlingen tabel
  async function fetchStudentData(userId) {
    try {
      const { data, error: fetchError } = await supabase
        .from('leerlingen')
        .select('*')
        .eq('auth_user_id', userId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      return data;
    } catch (err) {
      return null;
    }
  }

  // Haal klassen op voor een leerling
  async function fetchStudentClasses(studentId) {
    try {
      const { data, error: fetchError } = await supabase
        .from('klas_leerlingen')
        .select(`
          klas_id,
          klassen (
            id,
            naam,
            vak,
            schooljaar,
            school_id
          )
        `)
        .eq('leerling_id', studentId);

      if (fetchError) throw fetchError;
      return (data || []).map(row => row.klassen).filter(Boolean);
    } catch (err) {
      return [];
    }
  }

  // Load student data when Supabase auth changes
  useEffect(() => {
    // Check huidige sessie bij mount
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const student = await fetchStudentData(session.user.id);
        if (student) {
          setStudentUser({
            id: student.id,
            authUserId: session.user.id,
            email: session.user.email,
            ...student
          });
          const classes = await fetchStudentClasses(student.id);
          setStudentClasses(classes);
        }
      }
      setLoading(false);
    }).catch((err) => {
      console.error('StudentAuthContext: getSession() failed:', err);
      setStudentUser(null);
      setStudentClasses([]);
      setLoading(false);
    });

    // Luister naar toekomstige auth wijzigingen
    // BELANGRIJK: Geen await op Supabase queries hier (zelfde deadlock-risico als AuthContext)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          fetchStudentData(session.user.id).then(student => {
            if (student) {
              setStudentUser({
                id: student.id,
                authUserId: session.user.id,
                email: session.user.email,
                ...student
              });
              fetchStudentClasses(student.id).then(classes => {
                setStudentClasses(classes);
              });
            } else {
              setStudentUser(null);
              setStudentClasses([]);
            }
            // Pas klaar-melden als de leerling-data bekend is. Eerder stond
            // setLoading(false) buiten deze .then: bij een page-load/refresh op
            // een /student/*-route zag StudentProtectedRoute dan even
            // loading=false + studentUser=null → bounce naar login/dashboard.
            setLoading(false);
          });
        } else {
          setStudentUser(null);
          setStudentClasses([]);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  /**
   * Initialize student after Entree login callback.
   * Creates or updates the leerlingen record in Supabase.
   */
  async function initializeStudent(entreeData) {
    try {
      // Echte login wint altijd van een achtergebleven demo-vlag
      deactivateStudentDemo();
      setLoading(true);
      setError(null);

      const { authUserId, entreeId, displayName, schoolId } = entreeData;

      // SECURITY DEFINER RPC — bypass RLS timing-issues na Entree signUp
      const { data: rpcData, error: rpcError } = await supabase.rpc('ensure_leerling', {
        p_auth_user_id: authUserId,
        p_entree_id: entreeId || null,
        p_display_name: displayName || null,
        p_school_id: schoolId || null,
      });

      if (rpcError) throw rpcError;
      const student = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (!student) throw new Error('Kon leerling niet aanmaken');

      setStudentUser({
        id: student.id,
        authUserId,
        ...student
      });

      const classes = await fetchStudentClasses(student.id);
      setStudentClasses(classes);

      return student;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  /**
   * Refresh student data (e.g., after being added to a class)
   */
  // 21-09-2026: wordt nu ook aangeroepen bij "Vernieuwen" en bij het openen van
  // een portaaltab (useVakUniversum). Alleen een nieuwe state zetten als er echt
  // iets veranderd is, anders herladen alle tabs twee keer voor niets.
  async function refreshStudentData() {
    if (!studentUser?.authUserId) return;

    try {
      const student = await fetchStudentData(studentUser.authUserId);
      if (student) {
        setStudentUser(prev => {
          const samengevoegd = { ...prev, ...student };
          return JSON.stringify(samengevoegd) === JSON.stringify(prev) ? prev : samengevoegd;
        });
        const classes = await fetchStudentClasses(student.id);
        setStudentClasses(prev => {
          const sleutel = (lijst) => (lijst || []).map((k) => `${k.id}|${k.naam}|${k.vak || ''}`).sort().join(';');
          return sleutel(prev) === sleutel(classes) ? prev : classes;
        });
      }
    } catch (err) {
      // Silently fail
    }
  }

  /**
   * Logout student
   */
  async function logout() {
    try {
      deactivateStudentDemo();
      await supabase.auth.signOut();
      setStudentUser(null);
      setStudentClasses([]);
    } catch (err) {
      throw err;
    }
  }

  /**
   * DEV-ONLY: log in als een test-leerling met mock-data.
   * Geen Supabase auth, geen Entree. Alleen voor lokale UI-tests.
   */
  function loginAsTestStudent(seedStudent) {
    if (process.env.NODE_ENV !== 'development') {
      console.warn('loginAsTestStudent is only available in development');
      return;
    }
    activateStudentDemo();
    setStudentUser({
      id: seedStudent.id,
      authUserId: `test-${seedStudent.id}`,
      email: `${seedStudent.display_name.toLowerCase().replace(/\s+/g, '.')}@test.local`,
      display_name: seedStudent.display_name,
      school_id: seedStudent.school_id,
      klas: seedStudent.klas,
      _isTestStudent: true,
    });
    setStudentClasses(seedStudent.classes || []);
    setLoading(false);
  }

  /**
   * Check if student is in a specific class
   */
  function isInClass(classId) {
    return studentClasses.some(c => c.id === classId);
  }

  /**
   * Get class IDs for the student
   */
  function getClassIds() {
    return studentClasses.map(c => c.id);
  }

  const value = {
    studentUser,
    studentClasses,
    loading,
    error,
    initializeStudent,
    refreshStudentData,
    logout,
    loginAsTestStudent,
    isInClass,
    getClassIds,
    schoolFeatures,
    heeftFeature,
    isAuthenticated: !!studentUser,
    isTestStudent: !!studentUser?._isTestStudent
  };

  return (
    <StudentAuthContext.Provider value={value}>
      {children}
    </StudentAuthContext.Provider>
  );
}
